import React, { useState, useEffect, useMemo } from "react";
import { FormFooter } from "@/components/FormFooter";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { productosAPI, type ProductoGrupo } from "@/lib/api";
import { previewCodigoFromNombre } from "@/lib/codigoRubroSlug";
import { formatRubrosApiError } from "./rubrosApiErrors";

interface RubrosABMProps {
  item: ProductoGrupo | null;
  onBack: () => void;
  onSaved?: () => void;
}

export function RubrosABM({ item, onBack, onSaved }: RubrosABMProps) {
  const [nombre, setNombre] = useState("");
  const [descripcion, setDescripcion] = useState("");
  const [activo, setActivo] = useState(true);
  const [hasChanges, setHasChanges] = useState(false);
  const [loading, setLoading] = useState(false);

  const previewCodigo = useMemo(
    () => previewCodigoFromNombre(nombre, "grupo"),
    [nombre]
  );

  useEffect(() => {
    if (item) {
      setNombre(item.nombre);
      setDescripcion(item.descripcion ?? "");
      setActivo(item.activo);
    } else {
      setNombre("");
      setDescripcion("");
      setActivo(true);
    }
    setHasChanges(false);
  }, [item]);

  const handleSave = async () => {
    if (!nombre.trim()) {
      toast.error("El nombre es obligatorio");
      return;
    }

    setLoading(true);
    try {
      if (item) {
        await productosAPI.updateGrupo(item.codigo, {
          nombre: nombre.trim(),
          descripcion: descripcion.trim() || "",
          activo,
        });
        toast.success("Rubro actualizado");
      } else {
        const created = await productosAPI.createGrupo({
          nombre: nombre.trim(),
          descripcion: descripcion.trim() || "",
          activo,
        });
        toast.success(`Rubro creado (${created.codigo})`);
      }
      setHasChanges(false);
      onSaved?.();
      setTimeout(() => onBack(), 400);
    } catch (error: unknown) {
      console.error(error);
      toast.error(formatRubrosApiError(error, "Error al guardar el rubro"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6 mt-2">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <Label htmlFor="rubro-codigo">Código</Label>
          <Input
            id="rubro-codigo"
            value={item ? item.codigo : previewCodigo}
            disabled
            className="bg-muted"
            tabIndex={1}
          />
          {!item && (
            <p className="text-xs text-muted-foreground mt-1.5">
              Vista previa desde el nombre. El servidor asigna el código definitivo; si ya existe,
              añade sufijo -2, -3, etc.
            </p>
          )}
        </div>
        <div>
          <Label htmlFor="rubro-activo" className="block mb-2">
            Activo
          </Label>
          <label className="flex items-center gap-2 text-sm">
            <input
              id="rubro-activo"
              type="checkbox"
              checked={activo}
              onChange={(e) => {
                setActivo(e.target.checked);
                setHasChanges(true);
              }}
              className="w-4 h-4 rounded border-input"
            />
            <span>Rubro visible en listados</span>
          </label>
        </div>
      </div>

      <div>
        <Label htmlFor="rubro-nombre">Nombre</Label>
        <Input
          id="rubro-nombre"
          value={nombre}
          onChange={(e) => {
            setNombre(e.target.value);
            setHasChanges(true);
          }}
          placeholder="Nombre del rubro"
          tabIndex={2}
        />
      </div>

      <div>
        <Label htmlFor="rubro-desc">Descripción (opcional)</Label>
        <Input
          id="rubro-desc"
          value={descripcion}
          onChange={(e) => {
            setDescripcion(e.target.value);
            setHasChanges(true);
          }}
          placeholder="Notas internas"
          tabIndex={3}
        />
      </div>

      <FormFooter
        onBack={onBack}
        backDisabled={loading}
        backTabIndex={5}
        onSave={handleSave}
        saveDisabled={!hasChanges || loading}
        saveTabIndex={4}
        saving={loading}
      />
    </div>
  );
}
